// ─── Shared Quotation Document mapping helpers ────────────────────────────────
// Single canonical path: Backend snapshot → QuotationDocumentData.
// These helpers hold NO pricing / FX / total logic — they only reshape fields.

import type {
  DocumentLocale,
  QuotationDocumentSeller,
  QuotationDocumentBuyer,
  QuotationDocumentItem,
  QuotationFinishingItem,
} from '@/quotation/document/types';
import type {
  BuyerSnapshot,
  SellerSnapshot,
  MoneyLineFinishing,
  QuoteLineItem,
} from '@/lib/quotationTypes';

/**
 * Legacy fallback seller. Used ONLY when a quotation has no immutable
 * seller snapshot (§6). A NEW quotation with a seller snapshot must NOT be
 * overwritten by this constant.
 */
export const DEFAULT_SELLER: QuotationDocumentSeller = {
  companyName: 'GBCOSPACK',
  legalName: 'GB Cosmetic Packaging Co., Ltd.',
  address: '12F, Gangnam Finance Center, 152 Teheran-ro, Gangnam-gu, Seoul',
  country: 'Republic of Korea',
  telephone: '+82-2-1234-5678',
  email: 'trade@gbcospack.com',
  website: 'www.gbcospack.com',
};

/** Normalize Backend document_locale to the canonical DocumentLocale. */
export function normalizeDocumentLocale(value: string | null | undefined): DocumentLocale {
  if (!value) return 'en';
  const v = value.trim();
  if (v === 'ko' || v === 'ko-KR' || v === 'ko_KR') return 'ko';
  if (
    v === 'zh-CN' ||
    v === 'zh' ||
    v === 'zh_CN' ||
    v === 'zh-Hans' ||
    v === 'zh-CN-Hans'
  ) {
    return 'zh-CN';
  }
  return 'en';
}

/**
 * Convert a Backend decimal string / number to a JS number WITHOUT recalculating.
 * Preserves the distinction between `0` (explicit zero) and null/undefined (absent):
 *   - "0.000000" → 0
 *   - null / undefined / "" → undefined
 */
export function toNumber(value: string | number | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

/** Map applied FX rate to canonical exchangeRate (omitted when identity/absent). */
export function mapExchangeRate(appliedFxRate: string | null | undefined): number | undefined {
  const rate = toNumber(appliedFxRate);
  return rate != null && rate !== 1 ? rate : undefined;
}

/** Map an immutable Seller snapshot → canonical seller (legacy fallback when absent). */
export function mapSeller(snapshot: SellerSnapshot | null | undefined): QuotationDocumentSeller {
  if (!snapshot || !snapshot.companyName) return { ...DEFAULT_SELLER };
  return {
    companyName: snapshot.companyName,
    // Backend snapshot exposes a "tagline", not a legal name — leave legalName unset.
    address: snapshot.address ?? undefined,
    country: snapshot.country ?? undefined,
    // Backend snapshot field is "contact" (not "telephone").
    telephone: snapshot.contact ?? undefined,
    email: snapshot.email ?? undefined,
    website: snapshot.website ?? undefined,
  };
}

/** Map an immutable Buyer snapshot → canonical buyer (null when absent). */
export function mapBuyer(snapshot: BuyerSnapshot | null | undefined): QuotationDocumentBuyer | null {
  if (!snapshot) return null;
  return {
    companyName: snapshot.companyName ?? undefined,
    // Backend snapshot field is "contactPerson" (not "contactName").
    contactName: snapshot.contactPerson ?? undefined,
    address: snapshot.address ?? undefined,
    country: snapshot.country ?? undefined,
    email: snapshot.email ?? undefined,
    phone: snapshot.phone ?? undefined,
  };
}

/** Map a finishing snapshot line → canonical finishing item. */
export function mapFinishing(f: MoneyLineFinishing): QuotationFinishingItem {
  return {
    sourceType: f.sourceType ?? undefined,
    sourceCode: f.sourceCode ?? undefined,
    displayName: f.methodName,
    // Backend stores only pantoneColor — map it to the canonical "pantone" field.
    pantone: f.pantoneColor ?? undefined,
    unitPrice: toNumber(f.unitPrice),
    amount: toNumber(f.amount),
  };
}

/** Map a line item (product + finishings) → canonical document item. */
export function mapItem(item: QuoteLineItem): QuotationDocumentItem {
  const p = item.product;
  return {
    id: item.productId,
    sku: p.sku ?? undefined,
    productName: p.name,
    categoryL1Code: p.categoryL1Code ?? undefined,
    categoryL1Display: p.categoryL1Display ?? undefined,
    categoryL2Code: p.categoryL2Code ?? undefined,
    categoryL2Display: p.categoryL2Display ?? undefined,
    materialCode: p.materialCode ?? undefined,
    materialDisplay: p.materialDisplay ?? undefined,
    capacity: p.capacity ?? undefined,
    specification: p.specification ?? undefined,
    productImageId: p.productImageId ?? undefined,
    productImageUrl: p.productImageUrl ?? null,
    quantity: item.quantity,
    unitPrice: toNumber(p.unitPrice) ?? 0,
    amount: toNumber(p.amount) ?? 0,
    finishing: (item.finishings ?? []).map(mapFinishing),
  };
}